import { FC } from 'react'

import styled from 'styled-components'
import { Copy } from 'lucide-react'

import { Offer } from 'src/Redux/api/offersApi'
import DefaultDocument from 'src/Assets/document.svg?react'
import Cert from 'src/Assets/certificate.svg?react'
import Trash from 'src/Assets/trash.svg?react'
import { getValueOrFallback } from 'src/Helpers/functions.tsx'
import { colors, device } from 'src/Helpers/constants.ts'
import { IconButton } from 'src/Components/Button/IconButton.tsx'
import { Role, useGetProfileQuery } from 'src/Redux/api/authApi.ts'
import { formatClientName } from 'src/Helpers/organizationalForm'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f7f9fc;
  padding: 10px;
  gap: 30px;

  @media ${device.tabletMax} {
    padding: 20px;
  }
`

const ParametersContainer = styled.div`
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: 1fr 0.4fr 0.4fr 0.8fr 1fr;
  position: relative;

  @media ${device.tabletMax} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`

const Parameter = styled.span`
  font-size: 12px;
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: ${colors.thirdGray};
  font-weight: 600;
  justify-content: center;
  flex-shrink: 0;
`

const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  cursor: pointer;
`

const Name = styled.span`
  font-size: 14px;
  width: 100%;
  text-align: start;
  color: ${colors.thirdGray};
  font-weight: 600;

  @media ${device.tabletMax} {
    max-width: unset;
  }
`
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  gap: 10px;

  @media ${device.tabletMax} {
    position: absolute;
    top: 0;
    right: 0;
  }
`

const StyledButton = styled(IconButton)`
  background-color: ${colors.fourthBlue};

  &:hover {
    background-color: ${colors.fourthBlue};
  }
  margin-left: auto;
`

const StyledButtonRed = styled(IconButton)`
  background-color: ${colors.red};

  &:hover {
    background-color: ${colors.red};
  }
  margin-left: auto;
`

interface OfferItemProps {
  offer: Offer
  onViewOffer: (id: number) => void
  onCreateContract: (offerNumber: number) => void
  onCloneOffer: (id: number) => void
  onDeleteOffer: (id: number) => void
}

export const OfferItem: FC<OfferItemProps> = ({
  offer,
  onCreateContract,
  onViewOffer,
  onCloneOffer,
  onDeleteOffer,
}) => {
  const { offerNumber, client, createDate, id, contractNumber, user } = offer
  const { data: currentUser } = useGetProfileQuery()

  const formatDate = (dateString?: string): string => {
    if (!dateString) {
      return '-'
    }
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU')
  }

  const onCreateContractClick = (): void => {
    onCreateContract(Number(id))
  }

  const onViewOfferClick = (): void => {
    onViewOffer(Number(id))
  }

  const onDeleteOfferClick = (): void => {
    onDeleteOffer(Number(id))
  }

  const onCloneOfferClick = (): void => {
    onCloneOffer(Number(id))
  }

  const canDelete =
    currentUser?.role === Role.ADMIN || currentUser?.role === Role.LEAD_MANAGER

  return (
    <Container data-testid='offer-item'>
      <ParametersContainer>
        <NameWrapper onClick={onViewOfferClick} data-testid='view-offer'>
          <DefaultDocument width={50} height={50} />
          <Name>{getValueOrFallback(formatClientName(client))}</Name>
        </NameWrapper>

        <Parameter data-testid='offer-number'>
          {getValueOrFallback(offerNumber)}
        </Parameter>
        <Parameter>{formatDate(createDate)}</Parameter>
        <Parameter>
          {user?.firstName && user?.lastName
            ? `${user.firstName} ${user.lastName}`
            : getValueOrFallback(user?.email)}
        </Parameter>

        <ButtonContainer>
          {!contractNumber && (
            <StyledButton
              onClick={onCreateContractClick}
              tooltiptext={'Сформировать договор'}
            >
              <Cert />
            </StyledButton>
          )}
          <StyledButton
            onClick={onCloneOfferClick}
            tooltiptext={'Клонировать коммерческое предложение'}
            data-testid='clone-offer'
          >
            <Copy />
          </StyledButton>
          {canDelete && (
            <StyledButtonRed
              onClick={onDeleteOfferClick}
              tooltiptext={'Удалить коммерческое предложение'}
              data-testid='delete-offer'
            >
              <Trash />
            </StyledButtonRed>
          )}
        </ButtonContainer>
      </ParametersContainer>
    </Container>
  )
}
